import { TenantInfo } from '../services/hotel.service';

export type PageType = 'auth' | 'chat';

/**
 * Update page title and meta description based on current page type
 */
export function updatePageMetadata(tenant: TenantInfo, pageType: PageType): void {
  // Get page-specific data
  const pageTitle = tenant.pageTitle?.[pageType];
  const pageDescription = tenant.pageDescription?.[pageType];
  const logoUrl = tenant.logo_url;
  const currentUrl = window.location.href;
  
  // Generate keywords based on page type and hotel info
  const baseKeywords = `${tenant.name}, hotel, hospitality`;
  const pageKeywords = pageType === 'chat' 
    ? `${baseKeywords}, AI assistant, chat support, guest assistance, concierge`
    : `${baseKeywords}, guest portal, check-in, services, reservations`;

  // Update page title
  if (pageTitle) {
    document.title = pageTitle;
    updateMetaTag('og:title', pageTitle, 'property');
    updateMetaTag('twitter:title', pageTitle, 'name');
    console.log(`📄 Page title updated (${pageType}):`, pageTitle);
  }

  // Update all meta tags
  if (pageDescription) {
    // Basic meta tags
    updateMetaTag('description', pageDescription);
    updateMetaTag('keywords', tenant.keywords || pageKeywords);
    updateMetaTag('author', tenant.name);
    updateMetaTag('og:description', pageDescription, 'property');
    updateMetaTag('twitter:description', pageDescription, 'name');

    console.log(`📝 Meta description updated (${pageType}):`, pageDescription.substring(0, 100) + '...');
  }

  if (logoUrl) {
    updateMetaTag('og:image', logoUrl, 'property');
    updateMetaTag('twitter:image', logoUrl, 'name');
  }
  updateMetaTag('og:url', currentUrl, 'property');
  updateMetaTag('og:type', 'website', 'property');
  updateMetaTag('og:site_name', tenant.name, 'property');

  // Twitter Cards
  updateMetaTag('twitter:card', 'summary_large_image', 'name');
  // Additional SEO tags
  updateMetaTag('robots', 'index, follow', 'name');
  updateMetaTag('viewport', 'width=device-width, initial-scale=1.0', 'name');
  updateMetaTag('theme-color', tenant.brandDetails?.brand?.colors?.primary || '#4C0D16', 'name');

  // Update favicon (same for all pages)
  if (tenant.faviconURL) {
    updateFavicon(tenant.faviconURL);
    console.log(`🖼️ Favicon updated:`, tenant.faviconURL);
  }


  // Add structured data (JSON-LD)
  updateStructuredData(tenant, pageType);

  console.log(`🔍 SEO meta tags updated for ${pageType} page`);
}

/**
 * Update meta tag (supports both name and property attributes)
 */
function updateMetaTag(nameOrProperty: string, content: string, attributeType: 'name' | 'property' = 'name'): void {
  // Remove existing meta tag
  const selector = `meta[${attributeType}="${nameOrProperty}"]`;
  const existingMeta = document.querySelector(selector);
  if (existingMeta) {
    existingMeta.remove();
  }

  // Create new meta tag
  const meta = document.createElement('meta');
  meta.setAttribute(attributeType, nameOrProperty);
  meta.content = content;
  document.head.appendChild(meta);
}

/**
 * Update favicon
 */
function updateFavicon(faviconURL: string): void {
  // Remove existing favicon links
  const existingLinks = document.querySelectorAll('link[rel*="icon"]');
  existingLinks.forEach(link => link.remove());

  // Create new favicon link
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/x-icon';
  link.href = faviconURL;
  document.head.appendChild(link);
}


/**
 * Set initial page metadata when tenant data is loaded
 */
export function setInitialPageMetadata(tenant: TenantInfo): void {
  // Set auth page metadata by default (since most entry points are auth pages)
  updatePageMetadata(tenant, 'auth');
}

/**
 * Get page type based on current route
 */
export function getCurrentPageType(pathname: string): PageType {
  // Chat pages
  if (pathname.includes('/myhotelspace')) {
    return 'chat';
  }
  
  // All other pages are considered auth pages
  return 'auth';
}

/**
 * Initialize page metadata based on current route and tenant data
 */
export function initializePageMetadata(tenant: TenantInfo, pathname: string): void {
  const pageType = getCurrentPageType(pathname);
  updatePageMetadata(tenant, pageType);
}

/**
 * Update structured data (JSON-LD) for better SEO
 */
function updateStructuredData(tenant: TenantInfo, pageType: PageType): void {
  // Remove existing structured data
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }

  // Create structured data object
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Hotel",
    "name": tenant.name,
    "description": tenant.pageDescription?.[pageType] || `Welcome to ${tenant.name}`,
    "url": window.location.origin,
    ...(tenant.logo_url && { "logo": tenant.logo_url }),
    ...(tenant.faviconURL && { "image": tenant.faviconURL }),
    "sameAs": [
      window.location.origin
    ],
    "potentialAction": {
      "@type": "ReserveAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": window.location.origin,
        "actionPlatform": [
          "http://schema.org/DesktopWebPlatform",
          "http://schema.org/MobileWebPlatform"
        ]
      }
    }
  };

  // Add page-specific structured data
  if (pageType === 'chat') {
    structuredData["offers"] = {
      "@type": "Offer",
      "description": "24/7 AI-powered guest assistance and concierge services",
      "category": "Guest Services"
    };
  }

  // Create and append script tag
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(structuredData, null, 2);
  document.head.appendChild(script);

  console.log(`🏢 Structured data (JSON-LD) updated for ${pageType} page`);
}

